import React from 'react';
import moment from 'moment';
import 'moment/locale/en-gb';
import { IconClasses, Icon } from '@blueprintjs/core';
import { VXFilterNote } from 'oecd-visions';
import {
  isEmpty,
  get,
  map,
  reduce,
  values,
  includes,
  each,
  omit,
  sortBy,
  isString,
  remove,
  isArray,
  cloneDeep,
  size,
  sum,
} from 'lodash';
import {
  UI_FACET_CATEGORY,
  CATEGORY_SEARCH_FACETS,
  CONCEPT_SEARCH_FACETS,
  UI_FACETS_CONFIG,
  SEARCH_FACETS
} from './constants'
import messages from './messages';
import noImagePng from '../assets/images/no_image.png'
import { Link } from '../views/visions/vx-link';

export const parseDate = (date) => !isEmpty(date) ? moment(date) : date;

export const buildUiFacets = (facets, query, expandedNodeIds) => {
  const categoryFacets = {};
  const selectedFacets = extractFacetIdsFromLocationQuery(query);
  const uiFacets = reduce(
    facets,
    (memo, facet, id) => {
      if (includes(CATEGORY_SEARCH_FACETS, id)) {
        categoryFacets[id] = facet;
      } else if (includes(CONCEPT_SEARCH_FACETS, id)) {
        const isConceptRelated = SEARCH_FACETS.RELATED_CONCEPTS === id;
        const facetValues = mapConceptFacetValues(
          facet,
          get(selectedFacets, id, []),
          get(expandedNodeIds, id, []),
          isConceptRelated,
        );
        if (!isConceptRelated || !isEmpty(get(facetValues, '[0].childNodes'))) {
          memo[id] = {
            conf: UI_FACETS_CONFIG[id],
            data: facetValues,
          }
        }
      } else {
        memo[id] = {
          conf: UI_FACETS_CONFIG[id],
          data: mapNormalFacetValues(facet, get(selectedFacets, id, [])),
        };
      }
      return memo;
    },
    {}
  );

  const categoryExpandedIds = get(expandedNodeIds, UI_FACET_CATEGORY, []);
  const categorySelectedIds = get(selectedFacets, UI_FACET_CATEGORY);
  uiFacets[UI_FACET_CATEGORY] = {
    conf: UI_FACETS_CONFIG[UI_FACET_CATEGORY],
    ...buildCategoryFacets(categoryFacets, categorySelectedIds, categoryExpandedIds),
  };

  return uiFacets;
};

function mapConceptFacetValues(facet, selectedIds, expandeIds, defaultExpanded) {
  const _mapValue = parentPath => (val, idx) => {
    const isRoot = isEmpty(parentPath);
    const path = isRoot ? [idx] : [...parentPath, idx];
    const children = get(val, 'children', get(val, 'relatedConcepts'));
    const nodeDisabled = (defaultExpanded && isRoot);
    return {
      id: val.name,
      label: val.value,
      secondaryLabel: <VXFilterNote>{val.count}</VXFilterNote>,
      childNodes: map(children, _mapValue(path)),
      hasCaret: !isEmpty(children),
      isExpanded: includes(expandeIds, val.name) || defaultExpanded,
      isSelected: includes(selectedIds, val.name),
      className: nodeDisabled ? 'pt-tree-node-disabled' : 'pt-tree-node-normal',
      path,
      depth: size(path) - 1,
      disabled: nodeDisabled
    }
  };

  return map(sortBy(get(facet, 'facetValues', []), 'value'), _mapValue(null));
}

function mapNormalFacetValues(facet, selectedIds) {
  return map(sortBy(get(facet, 'facetValues', []), 'value'), (val, valIdx) => ({
    ...buildFacetValue(val, selectedIds),
    depth: 0,
    path: [valIdx],
  }));
}

function buildFacetValue(val, selectedIds = [], expandedIds = []) {
  return {
    id: get(val, 'name'),
    label: get(val, 'value'),
    secondaryLabel: <VXFilterNote>{get(val, 'count')}</VXFilterNote>,
    isSelected: includes(selectedIds, val.name),
    isExpanded: includes(expandedIds, val.name),
  }
}

function buildCategoryFacets(facets, selectedNodeIds, expandedNodeIds) {
  const segmentValues = get(facets, `${SEARCH_FACETS.GPC_SEGMENT}.facetValues`, []);
  const hierarchicalFacets = reduce(
    segmentValues,
    (memo, facetValue) => {
      const uiFacetvalue = {
        ...buildFacetValue(facetValue, selectedNodeIds, expandedNodeIds),
        qFacetName: SEARCH_FACETS.GPC_SEGMENT,
        children: {}
      };
      memo[uiFacetvalue.id] = uiFacetvalue;
      return memo;
    },
    {}
  );

  const families = {};
  const familyValues = get(facets, `${SEARCH_FACETS.GPC_FAMILY}.facetValues`, []);
  each(
    familyValues,
    (facetValue) => {
      const uiFacetvalue = {
        ...buildFacetValue(facetValue, selectedNodeIds, expandedNodeIds),
        qFacetName: SEARCH_FACETS.GPC_FAMILY,
        children: {},
      };
      families[uiFacetvalue.id] = { ...uiFacetvalue, parentname: facetValue.parentname };
      each(facetValue.parentname, (parentname) => {
        hierarchicalFacets[parentname].children[uiFacetvalue.id] = uiFacetvalue;
      });
    }
  );

  const classes = {};
  const classValues = get(facets, `${SEARCH_FACETS.GPC_CLASS}.facetValues`, []);
  each(
    classValues,
    (facetValue) => {
      const uiFacetvalue = {
        ...buildFacetValue(facetValue, selectedNodeIds, expandedNodeIds),
        qFacetName: SEARCH_FACETS.GPC_CLASS,
        children: {},
      };
      classes[uiFacetvalue.id] = { ...uiFacetvalue, parentname: facetValue.parentname };
      each(facetValue.parentname, (_pn) => {
        each(families[_pn].parentname, (__pn) => {
          hierarchicalFacets[__pn].children[_pn].children[uiFacetvalue.id] = uiFacetvalue;
        });
      });
    }
  );

  const brickValues = get(facets, `${SEARCH_FACETS.GPC_BRICK}.facetValues`, []);
  each(
    brickValues,
    (facetValue) => {
      const uiFacetvalue = {
        ...buildFacetValue(facetValue, selectedNodeIds, expandedNodeIds),
        qFacetName: SEARCH_FACETS.GPC_BRICK
      };
      each(facetValue.parentname, (_pn) => {
        each(classes[_pn].parentname, (__pn) => {
          each(families[__pn].parentname, (___pn) => {
            hierarchicalFacets[___pn].children[__pn].children[_pn].children[uiFacetvalue.id] = uiFacetvalue;
          });
        });
      });
    }
  );

  const data = map(sortBy(hierarchicalFacets, 'label'), (fs, fsIdx) => ({
    ...fs,
    children: null,
    path: [fsIdx],
    depth: 0,
    hasCaret: !isEmpty(fs.children),
    childNodes: map(sortBy(fs.children, 'label'), (ff, ffIdx) => ({
      ...ff,
      path: [fsIdx, ffIdx],
      depth: 1,
      children: null,
      hasCaret: !isEmpty(ff.children),
      childNodes: map(sortBy(ff.children, 'label'), (fc, fcIdx) => ({
        ...fc,
        path: [fsIdx, ffIdx, fcIdx],
        depth: 2,
        children: null,
        hasCaret: !isEmpty(fc.children),
        childNodes: map(sortBy(values(fc.children), 'label'), (fb, fbIdx) => ({
          ...fb,
          path: [fsIdx, ffIdx, fcIdx, fbIdx],
          depth: 3,
        }))
      }))
    }))
  }));

  return {
    data,
    valuesCount: sum([size(segmentValues), size(familyValues), size(classValues), size(brickValues)]),
  };
}

export function extractFacetIdsFromLocationQuery(query) {
  const searchFacetIds = values(SEARCH_FACETS);
  return reduce(query, (memo, q, id) => {
      if (includes(CATEGORY_SEARCH_FACETS, id)) {
        const _q = isString(q) ? [q] : q;
        memo[UI_FACET_CATEGORY] = [...memo[UI_FACET_CATEGORY], ..._q];
      } else if (includes(searchFacetIds, id)) {
        memo[id] = isString(q) ? [q] : q;
      }
      return memo;
    }, { [UI_FACET_CATEGORY]: [] }
  );
}

export function filterRecursive(list, predicate, childGetter = x => x.children) {
  const result = cloneDeep(list);

  const itemFilter = item => {
    const children = childGetter(item);
    if (isArray(children)) {
      remove(children, itemFilter);
    }
    return !(predicate(item) || (children && children.length > 0));
  };

  remove(result, itemFilter);
  return result;
}

export function spotlightTreeEngine(spotlight, items) {
  if (isEmpty(spotlight)) {
    return items;
  }

  const { term } = spotlight;
  if (isEmpty(term)) {
    return items;
  }

  const regExp = new RegExp(term, 'i');
  return filterRecursive(items, x => regExp.test(x.label), x => x.childNodes);
}

export const recallColumns = (config) => ([
  {
    dataKey: 'date',
    sortable: true,
    bsClass: 'col-md-1',
    intl: {
      title: messages.date.id,
    },
  },
  {
    dataKey: 'imageUri',
    bsClass: 'col-md-1',
    intl: {
      title: messages.picture.id,
    },
    cellRenderer(data) {
      let imageUri = get(data, 'imageUri');
      const imageSrc = isEmpty(imageUri)
        ? noImagePng
        : `${config.api}/getdocument.xqy?uri=${encodeURIComponent(imageUri)}`;

      return (
        <img
          alt=''
          style={{ width: '50px', height: '50px' }}
          onError={e => e.target.src = noImagePng}
          src={imageSrc}
        />
      )
    }
  },
  {
    dataKey: 'product.name',
    bsClass: 'col-md-3',
    sortable: true,
    intl: {
      title: messages.name.id,
    },
    cellRenderer(data) {
      return (
        <Link to={`recalls/${encodeURIComponent(get(data, 'uri'))}`}>
          {get(data, 'product.name')}
        </Link>
      );
    }
  },
  {
    dataKey: 'countryName',
    bsClass: 'col-md-2',
    sortable: true,
    intl: {
      title: messages.recallCountryName.id,
    },
  },
  {
    dataKey: 'manufacturer.country',
    bsClass: 'col-md-2',
    sortable: true,
    intl: {
      title: messages.manufacturerName.id,
    },
    cellRenderer(data) {
      return map(get(data, 'manufacturer.country', []), 'name').join(', ')
    }
  },
  {
    dataKey: 'language',
    bsClass: 'col-md-2',
    sortable: true,
    intl: {
      title: messages.language.id,
    },
  },
  {
    dataKey: 'extUrl',
    bsClass: 'col-md-1',
    intl: {
      title: messages.authoritiesLink.id,
    },
    cellRenderer(data) {
      return (
        <a href={get(data, 'extUrl')} target="_blank">
          <Icon iconName={IconClasses.SHARE} iconSize={Icon.SIZE_LARGE}/>
        </a>
      )
    },
  },
]);




// WEBPACK FOOTER //
// ./src/utils/index.js